import { useEffect, useMemo, useState } from 'react';
import {
    ShoppingBag, Smartphone, CreditCard, Package, Check,
    ArrowLeft, QrCode, CheckCircle2, IdCard, MapPin, ShieldCheck,
} from 'lucide-react';
import { notify } from '../../components/Notify';
import { getProdutosDisponiveisApi, getProfileApi, createPixPaymentApi, createCardPaymentApi, getPagamentoConfigApi } from '../../api';
import { getUser } from '../../auth/storage';
import DadosCarteirinhaForm, { DADOS_CARTEIRINHA_VAZIO, dadosCarteirinhaCompletos, montarDadosCadastro } from '../../components/DadosCarteirinhaForm';
import CardForm from '../../components/CardForm';
import FreteCalculator from './FreteCalculator';
import PixWaiting from './PixWaiting';
import './produtos.css';

const VIEW = { LIST: 'list', CHECKOUT: 'checkout', PIX: 'pix', DONE: 'done' };
const moeda = (v) => `R$ ${Number(v || 0).toFixed(2).replace('.', ',')}`;

function iconePorProduto(p) {
    if (p.fisica === 1 && p.digital === 1) return CreditCard;
    if (p.fisica === 1) return Package;
    return Smartphone;
}
function featuresPorProduto(p, correiosAtivo = true) {
    const f = [];
    if (p.digital === 1) f.push('Carteira digital imediata');
    if (p.fisica === 1) f.push(correiosAtivo ? 'Via física em PVC pelos Correios' : 'Via física em PVC');
    f.push('Validação por QR Code');
    f.push('Válida em todo o Brasil');
    return f;
}

export default function Produtos() {
    const [proximaAcao, setProximaAcao] = useState(null);
    const [correiosAtivo, setCorreiosAtivo] = useState(false);
    const [produtos, setProdutos] = useState(null);
    const [pendente, setPendente] = useState(null);
    const [view, setView] = useState(VIEW.LIST);
    const [produto, setProduto] = useState(null);
    const [frete, setFrete] = useState(null);
    const [aba, setAba] = useState('pix');
    const [pix, setPix] = useState(null);
    const [gerandoPix, setGerandoPix] = useState(false);
    const [pagandoCartao, setPagandoCartao] = useState(false);
    const [cartaoConfig, setCartaoConfig] = useState({ ativo: false, publicKey: '' });
    const [dados, setDados] = useState({ ...DADOS_CARTEIRINHA_VAZIO });
    // Checkout em duas etapas: 1 = dados + entrega, 2 = pagamento.
    const [passo, setPasso] = useState(1);
    // Modal de segurança que aparece na 1ª vez que o aluno abre o cartão.
    const [modalCartao, setModalCartao] = useState(false);
    const [modalCartaoVisto, setModalCartaoVisto] = useState(false);

    // Busca (ou re-busca) o estado da vitrine: o que oferecer, config de cartão
    // e frete. Chamado ao montar E depois de um pagamento aprovado, para que a
    // lista reflita na hora que não há mais nada a comprar (proxima_acao=NADA).
    const carregar = async () => {
        try {
            const [prodRes, perfilRes, cfgRes] = await Promise.all([
                getProdutosDisponiveisApi(),
                getProfileApi(),
                getPagamentoConfigApi().catch(() => null),
            ]);
            const prod = prodRes?.data;
            setCartaoConfig({ ativo: !!cfgRes?.data?.cartao_ativo, publicKey: cfgRes?.data?.public_key || '' });
            setProximaAcao(prod?.proxima_acao || 'COMPRAR_NOVA');
            setCorreiosAtivo(!!prod?.correios_ativo);
            setProdutos(prod?.produtos || []);
            setPendente(prod?.pendente || null);
            void perfilRes;
        } catch {
            setProximaAcao('COMPRAR_NOVA');
            setProdutos([]);
        }
    };

    useEffect(() => {
        let vivo = true;
        (async () => { if (vivo) await carregar(); })();
        return () => { vivo = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Upgrade = produto só-física (o aluno já tem a digital do ano).
    const ehUpgrade = produto?.fisica === 1 && produto?.digital === 0;
    // Dados da carteirinha (curso/foto/etc.) só são coletados quando há digital a
    // criar; no upgrade físico a digital já existe, então não pede de novo.
    const precisaDados = produto?.digital === 1;
    // Física SEMPRE exige frete (modo fixo SEDEX/Carta Reg ou Correios). Sem uma
    // modalidade escolhida, o pagamento não libera — regra do dono: física com frete.
    const exigeFrete = produto?.fisica === 1;
    const enderecoPronto = exigeFrete ? !!frete : true;
    const total = useMemo(
        () => Number(produto?.price || 0) + (frete?.freteValor || 0),
        [produto, frete]
    );

    const dadosOk = !precisaDados || dadosCarteirinhaCompletos(dados);
    // Só libera avançar para o pagamento com entrega e dados prontos.
    const podeAvancar = enderecoPronto && dadosOk;
    const faltandoPasso1 = !enderecoPronto
        ? 'Complete o endereço de entrega e escolha a modalidade para continuar.'
        : !dadosOk
            ? 'Preencha os dados da carteirinha (instituição, curso, nível e foto) para continuar.'
            : '';

    const iniciarCheckout = (p) => {
        setProduto(p); setFrete(null); setAba('pix'); setPix(null);
        setDados({ ...DADOS_CARTEIRINHA_VAZIO });
        setPasso(1); setModalCartao(false); setModalCartaoVisto(false);
        setView(VIEW.CHECKOUT);
    };
    const voltarLista = () => {
        setView(VIEW.LIST); setProduto(null); setFrete(null); setPix(null);
        setDados({ ...DADOS_CARTEIRINHA_VAZIO });
        setPasso(1); setModalCartao(false); setModalCartaoVisto(false);
    };
    // Voltar à vitrine DEPOIS de pagar: re-busca o estado (agora "nada a comprar").
    const voltarAposPagamento = async () => { await carregar(); voltarLista(); };

    // Abrir a aba do cartão passa pelo aviso de segurança na 1ª vez.
    const abrirCartao = () => { if (modalCartaoVisto) setAba('cartao'); else setModalCartao(true); };
    const confirmarCartao = () => { setModalCartaoVisto(true); setModalCartao(false); setAba('cartao'); };

    const pagarPix = async () => {
        if (!enderecoPronto) return notify('Confirme o endereço de entrega primeiro.');
        if (!dadosOk) return notify('Preencha os dados da carteirinha (instituição, curso, nível e foto).');
        setGerandoPix(true);
        try {
            const res = await createPixPaymentApi({
                productId: produto.id,
                userId: getUser()?.id,
                cepDestino: frete?.cepDestino,
                codigoServico: frete?.codigoServico,
                dadosCadastro: { ...montarDadosCadastro(dados), endereco: frete?.endereco || undefined },
            });
            setPix(res.data);
            setView(VIEW.PIX);
        } catch (err) {
            notify(err?.response?.data?.error || 'Não foi possível gerar o Pix.');
        } finally {
            setGerandoPix(false);
        }
    };

    // Retoma um Pix pendente (mesmo produto): o backend é idempotente e devolve
    // o MESMO Pix em aberto, sem cobrar de novo.
    const retomarPagamento = async () => {
        if (!pendente?.productId) return;
        setGerandoPix(true);
        try {
            const res = await createPixPaymentApi({ productId: pendente.productId, userId: getUser()?.id });
            setPix(res.data);
            setView(VIEW.PIX);
        } catch (err) {
            notify(err?.response?.data?.error || 'Não foi possível retomar o pagamento.');
        } finally {
            setGerandoPix(false);
        }
    };

    // Recebe (cardToken, installments, billing) do CardForm e cobra no cartão.
    const pagarCartao = async (cardToken, installments, billing) => {
        if (!enderecoPronto) return notify('Confirme o endereço de entrega primeiro.');
        if (precisaDados && !dadosOk) return notify('Preencha os dados da carteirinha (instituição, curso, nível e foto).');
        setPagandoCartao(true);
        try {
            const res = await createCardPaymentApi({
                productId: produto.id,
                cardToken,
                installments,
                billing,
                cepDestino: frete?.cepDestino,
                codigoServico: frete?.codigoServico,
                dadosCadastro: { ...(precisaDados ? montarDadosCadastro(dados) : {}), endereco: frete?.endereco || undefined },
            });
            if (res.data?.approved || res.data?.status === 'approved') {
                setView(VIEW.DONE);
            } else {
                notify('Cartão não aprovado. Confira os dados ou tente outro cartão.');
            }
        } catch (err) {
            notify(err?.response?.data?.error || 'Não foi possível processar o cartão.');
        } finally {
            setPagandoCartao(false);
        }
    };

    // ------------------------------- RENDER -------------------------------
    if (produtos === null) {
        return <div className="ueb-page"><div className="pcard" style={{ padding: 40, textAlign: 'center' }}><span className="pspin" style={{ color: 'var(--p-accent)' }} /></div></div>;
    }

    const Header = (
        <header className="ueb-page-head">
            <span className="ueb-page-ico"><ShoppingBag size={22} /></span>
            <div>
                <h1 className="ueb-page-title">Produtos</h1>
                <p className="ueb-page-sub">Emita ou renove sua carteira de estudante.</p>
            </div>
        </header>
    );

    if (proximaAcao === 'NADA') {
        return (
            <div className="ueb-page">
                {Header}
                <div className="ueb-empty pcard">
                    <span className="ueb-empty-ico"><CheckCircle2 size={28} /></span>
                    <strong>Tudo certo para este ano</strong>
                    <p>Você já tem a carteira do ano vigente. Não há nada para comprar agora.</p>
                </div>
            </div>
        );
    }

    if (proximaAcao === 'PAGAMENTO_PENDENTE' && view === VIEW.LIST) {
        const ehPix = pendente?.metodo === 'pix';
        return (
            <div className="ueb-page">
                {Header}
                <div className="ueb-empty pcard">
                    <span className="ueb-empty-ico" style={{ background: 'var(--p-warning-weak)', color: 'var(--p-warning)' }}><QrCode size={28} /></span>
                    <strong>Você tem um pagamento em aberto</strong>
                    <p>
                        {ehPix
                            ? 'Já existe um Pix gerado para a sua carteira deste ano. Retome para pagar — não geramos uma nova cobrança em cima da mesma carteirinha.'
                            : 'Seu pagamento está em processamento. Assim que for confirmado, sua carteira é liberada. Não é preciso pagar de novo.'}
                    </p>
                    {ehPix && (
                        <button className="pbtn pbtn--primary" onClick={retomarPagamento} disabled={gerandoPix} style={{ marginTop: 6 }}>
                            {gerandoPix ? <span className="pspin" /> : <><QrCode size={16} /> Retomar pagamento</>}
                        </button>
                    )}
                </div>
            </div>
        );
    }

    if (view === VIEW.PIX && pix) {
        return (
            <div className="ueb-page">
                {Header}
                <button className="pbtn pbtn--ghost pbtn--sm ueb-back" onClick={voltarLista}><ArrowLeft size={16} /> Cancelar e voltar</button>
                <PixWaiting pix={pix} onAprovado={() => setView(VIEW.DONE)} onCancelar={voltarLista} />
            </div>
        );
    }

    if (view === VIEW.DONE) {
        return (
            <div className="ueb-page">
                {Header}
                <div className="ueb-empty pcard">
                    <span className="ueb-empty-ico" style={{ background: 'var(--p-accent-weak)', color: 'var(--p-accent)' }}><CheckCircle2 size={30} /></span>
                    <strong>Pagamento aprovado!</strong>
                    <p>Sua carteira já está sendo processada. Acompanhe em “Minha carteirinha”.</p>
                    <button className="pbtn pbtn--primary" onClick={voltarAposPagamento} style={{ marginTop: 8 }}>Voltar aos produtos</button>
                </div>
            </div>
        );
    }

    if (view === VIEW.CHECKOUT && produto) {
        return (
            <div className="ueb-page">
                {Header}
                <button className="pbtn pbtn--ghost pbtn--sm ueb-back" onClick={voltarLista}><ArrowLeft size={16} /> Voltar aos produtos</button>

                {/* Passos */}
                <div className="ueb-steps">
                    <div className={`ueb-step ${passo === 1 ? 'is-active' : ''} ${passo > 1 ? 'is-done' : ''}`}>
                        <span className="ueb-step-num">{passo > 1 ? <Check size={14} /> : 1}</span>
                        <span className="ueb-step-label">Dados e entrega</span>
                    </div>
                    <span className="ueb-step-sep" />
                    <div className={`ueb-step ${passo === 2 ? 'is-active' : ''}`}>
                        <span className="ueb-step-num">2</span>
                        <span className="ueb-step-label">Pagamento</span>
                    </div>
                </div>

                <div className="ueb-co">
                    <div className="ueb-co-main">
                        {passo === 1 ? (
                            <>
                                {ehUpgrade && (
                                    <div className="palert palert--info">
                                        <Package size={18} />
                                        <span>
                                            <strong>Upgrade para carteirinha física.</strong> Você já tem a digital deste ano —
                                            isto adiciona a via física em PVC{exigeFrete
                                                ? ', entregue pelos Correios no endereço que você informar abaixo.'
                                                : ', enviada para o endereço de entrega que você informar abaixo.'}
                                        </span>
                                    </div>
                                )}

                                {exigeFrete && (
                                    <div className="pcard ueb-co-block">
                                        <FreteCalculator produtoValor={produto.price} onConfirmar={setFrete} />
                                    </div>
                                )}
                                {exigeFrete && frete && (
                                    <div className="palert palert--success"><Check size={16} /> Entrega por {frete.modalidadeNome} confirmada para {frete.endereco.cidade} - {frete.endereco.uf}.</div>
                                )}

                                {precisaDados && (
                                    <div className="pcard ueb-co-block">
                                        <h3 className="ueb-co-h3"><IdCard size={17} /> Dados da carteirinha</h3>
                                        <p className="pmuted" style={{ marginTop: -6, marginBottom: 14 }}>Usados para emitir sua carteira. Confira antes de pagar.</p>
                                        <DadosCarteirinhaForm dados={dados} setDados={setDados} onErro={notify} />
                                    </div>
                                )}

                                {!exigeFrete && !precisaDados && (
                                    <div className="pcard ueb-co-block">
                                        <p className="pmuted" style={{ margin: 0 }}>Confira o resumo ao lado e siga para o pagamento.</p>
                                    </div>
                                )}

                                <div className="ueb-co-nav">
                                    {!podeAvancar && <span className="ueb-co-nav-hint">{faltandoPasso1}</span>}
                                    <button className="pbtn pbtn--primary pbtn--lg" disabled={!podeAvancar} onClick={() => setPasso(2)}>
                                        Continuar para pagamento
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                {/* Resumo do que foi preenchido no passo 1, com atalho pra corrigir */}
                                <div className="pcard ueb-co-block ueb-co-confirm">
                                    {exigeFrete && frete && (
                                        <div className="ueb-co-confirm-linha">
                                            <MapPin size={16} />
                                            <span>Entrega por <strong>{frete.modalidadeNome}</strong> — {frete.endereco.cidade}/{frete.endereco.uf}</span>
                                            <button className="ueb-co-confirm-edit" onClick={() => setPasso(1)}>Alterar</button>
                                        </div>
                                    )}
                                    {precisaDados && (
                                        <div className="ueb-co-confirm-linha">
                                            <IdCard size={16} />
                                            <span>Dados da carteirinha preenchidos</span>
                                            <button className="ueb-co-confirm-edit" onClick={() => setPasso(1)}>Alterar</button>
                                        </div>
                                    )}
                                </div>

                                <div className="pcard ueb-co-block">
                                    <h3 className="ueb-co-h3"><CreditCard size={17} /> Pagamento</h3>
                                    <div className="ueb-co-tabs">
                                        <button className={`ueb-co-tab ${aba === 'pix' ? 'is-active' : ''}`} onClick={() => setAba('pix')}><QrCode size={16} /> Pix</button>
                                        <button className={`ueb-co-tab ${aba === 'cartao' ? 'is-active' : ''}`} onClick={abrirCartao}><CreditCard size={16} /> Cartão</button>
                                    </div>

                                    {aba === 'pix' ? (
                                        <div className="ueb-co-pay">
                                            <p className="pmuted">Pagamento instantâneo. A carteira é liberada assim que o Pix é confirmado.</p>
                                            <button className="pbtn pbtn--primary pbtn--block pbtn--lg" onClick={pagarPix} disabled={!enderecoPronto || !dadosOk || gerandoPix}>
                                                {gerandoPix ? <span className="pspin" /> : `Pagar ${moeda(total)} no Pix`}
                                            </button>
                                        </div>
                                    ) : cartaoConfig.ativo ? (
                                        <div className="ueb-co-pay">
                                            <p className="pmuted">Pagamento na hora. A carteira é liberada assim que o cartão é aprovado.</p>
                                            <CardForm
                                                publicKey={cartaoConfig.publicKey}
                                                botaoLabel={`Pagar ${moeda(total)} no cartão`}
                                                processando={pagandoCartao}
                                                disabled={!enderecoPronto || (precisaDados && !dadosOk)}
                                                enderecoPadrao={frete?.endereco || null}
                                                onPagar={pagarCartao}
                                            />
                                        </div>
                                    ) : (
                                        <div className="ueb-co-pay">
                                            <div className="palert palert--info">
                                                <CreditCard size={18} />
                                                <span>
                                                    O pagamento por cartão (Pagar.me / Stone) está temporariamente indisponível.
                                                    Use o Pix para concluir agora.
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>

                    <aside className="ueb-co-resumo pcard">
                        <h3 className="ueb-co-h3">Resumo</h3>
                        <div className="ueb-co-linha"><span>{produto.title}</span><strong>{moeda(produto.price)}</strong></div>
                        {exigeFrete && <div className="ueb-co-linha"><span>Frete {frete ? `(${frete.modalidadeNome})` : ''}</span><strong>{frete ? moeda(frete.freteValor) : '—'}</strong></div>}
                        <div className="ueb-co-total"><span>Total</span><strong>{moeda(total)}</strong></div>
                    </aside>
                </div>

                {/* Aviso de segurança ao pagar com cartão */}
                {modalCartao && (
                    <div className="ueb-modal-wrap" onClick={() => setModalCartao(false)}>
                        <div className="ueb-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                            <span className="ueb-modal-ico"><ShieldCheck size={26} /></span>
                            <h3 className="ueb-modal-title">Sua segurança em primeiro lugar</h3>
                            <p className="ueb-modal-text">
                                A UEB se preocupa com a integridade dos seus dados. Para pagar com cartão com ainda
                                mais tranquilidade, uma boa prática é usar um <strong>cartão virtual</strong> do seu
                                banco: você cria, faz o pagamento e exclui em seguida.
                            </p>
                            <div className="ueb-modal-actions">
                                <button className="pbtn pbtn--ghost" onClick={() => setModalCartao(false)}>Voltar</button>
                                <button className="pbtn pbtn--primary" onClick={confirmarCartao}>Entendi, continuar</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // VIEW.LIST
    const listaUpgrade = proximaAcao === 'COMPRAR_FISICA';
    return (
        <div className="ueb-page">
            {Header}

            {listaUpgrade && (
                <div className="palert palert--info" style={{ marginBottom: 16 }}>
                    <Package size={18} />
                    <span>
                        <strong>Sua carteirinha digital deste ano já está garantida.</strong> Se quiser, adicione a
                        <strong> via física em PVC</strong>{correiosAtivo ? ' com entrega pelos Correios.' : ' (envio combinado com a equipe).'}
                    </span>
                </div>
            )}

            <div className="ueb-prod-grid">
                {produtos.map((p) => {
                    const Icon = iconePorProduto(p);
                    const upgradeCard = p.fisica === 1 && p.digital === 0;
                    return (
                        <div key={p.id} className="pcard ueb-prod-card">
                            <div className="ueb-prod-top">
                                <span className="ueb-prod-ico"><Icon size={22} /></span>
                                {p.fisica === 1 && p.digital === 1 && <span className="pchip pchip--gold">Mais completo</span>}
                                {upgradeCard && <span className="pchip pchip--gold">Upgrade</span>}
                            </div>
                            <h3 className="ueb-prod-title">{p.title}</h3>
                            <p className="ueb-prod-desc">{p.description}</p>
                            <ul className="ueb-prod-features">
                                {featuresPorProduto(p, correiosAtivo).map((f) => <li key={f}><Check size={15} /> {f}</li>)}
                            </ul>
                            <div className="ueb-prod-foot">
                                <span className="ueb-prod-price">{moeda(p.price)}</span>
                                <button className="pbtn pbtn--primary" onClick={() => iniciarCheckout(p)}>
                                    {upgradeCard ? 'Fazer upgrade' : 'Comprar'}
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
